import {Formatters} from 'src/js/common';
import {MerieSchema} from 'src/js/common/types';
import {ENCOUNTER_DOCUMENT_ROUTE} from 'src/js/containers/encounter/encounter_constants';
import {ROUTE_HOSPITALIZATIONS} from 'src/js/containers/hospitalizations/hospitalizations_constants';

type RiskFactorLabel = MerieSchema.components['schemas']['RiskFactorLabelEnum'];
type RiskFactorLabelColors = Record<
  RiskFactorLabel,
  {
    color: string;
    order: number;
  }
>;
export const RISK_FACTOR_LABEL_COLORS: RiskFactorLabelColors = {
  ORGAN_DYSFUNCTION: {color: '#EDF5FF', order: 1},
  MEDICATION: {color: '#F6F2FF', order: 2},
  DIAGNOSIS: {color: '#DEF9FF', order: 3},
  OTHER: {color: '#F2F4F8', order: 4},
};

export function sortRiskFactorsData<T>(data: T[], oldRiskFactorIds: Set<number>): T[] {
  return data?.sort((first, second) => {
    const isOldKey = (v) => +oldRiskFactorIds.has(v.id);
    const isOldDiff = +(isOldKey(first) > isOldKey(second)) - +(isOldKey(first) < isOldKey(second));
    if (isOldDiff) return isOldDiff;

    const orderKey = (v) => RISK_FACTOR_LABEL_COLORS[v.labels?.[0]]?.order;
    const orderDiff = +(orderKey(first) > orderKey(second)) - +(orderKey(first) < orderKey(second));
    if (orderDiff) return orderDiff;

    const riskFactorKey = (v) => `${v.type.toUpperCase()} - ${v.narrowTherapeuticIndexMedication || ''}`;
    const riskFactorA = riskFactorKey(first);
    const riskFactorB = riskFactorKey(second);
    const riskFactorDiff = +(riskFactorA > riskFactorB) - +(riskFactorA < riskFactorB);
    if (riskFactorDiff) return riskFactorDiff;

    return 0;
  });
}

export function dataTransformFunction(data) {
  return {
    content: data.content.map((content) => ({
      ...content,
      maxSubRows: content.annotations.length,
    })),
  };
}

export function getHospitalizationDocumentLink(hospitalizationId: string, documentId: number | string, versionSuffix: string): string {
  return `${ROUTE_HOSPITALIZATIONS}${versionSuffix ?? ''}/${hospitalizationId}${ENCOUNTER_DOCUMENT_ROUTE}/${documentId}`;
}

type BaseMedicationAnnotation = {
  medicationData?: {
    atc3rdLevelCode?: string;
    dictionaryMedicationName?: string;
    originalMedicationName?: string;
  };
};
export function sortMedicationData<T extends BaseMedicationAnnotation>(data: T[]): T[] {
  const sortedData = [...data];
  return sortedData.sort((a, b) => {
    const key1 = (annotation: T) => annotation.medicationData?.atc3rdLevelCode ?? '';
    const atc3rdLevelCodeA = key1(a);
    const atc3rdLevelCodeB = key1(b);
    if (atc3rdLevelCodeA < atc3rdLevelCodeB) {
      return -1;
    }
    if (atc3rdLevelCodeA > atc3rdLevelCodeB) {
      return 1;
    }

    const key2 = (annotation) =>
      annotation.medicationData?.dictionaryMedicationName ?? annotation.medicationData?.originalMedicationName ?? '';
    const nameA = key2(a);
    const nameB = key2(b);
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }

    return 0;
  });
}

type MedicationAnnotation = MerieSchema.components['schemas']['TimelineEventAnnotationDto'];
export function getFormattedAnnotationParts(annotation: MedicationAnnotation, formatters: Formatters): (string | undefined | false)[] {
  const {t, tEnum} = formatters;
  const medicationName =
    (annotation.medicationData?.dictionaryMedicationName ?? annotation.medicationData?.originalMedicationName ?? '') +
    (annotation.medicationData?.strength ? ` ${annotation.medicationData.strength}` : '');
  const routeOfAdministration =
    (annotation.medicationData?.routeOfAdministration || 'UNKNOWN') !== 'UNKNOWN' &&
    tEnum('PatientMedicationRouteOfAdministrationTypeEnum', annotation.medicationData?.routeOfAdministration as string);
  let usageType =
    (annotation.medicationData?.usageType || 'UNKNOWN') !== 'UNKNOWN' &&
    tEnum('PatientMedicationUsageTypeEnum', annotation.medicationData?.usageType as string);
  if (annotation.medicationData?.isDiscontinued) {
    usageType = t('timeline.annotationTooltip.usageType.isDiscontinued');
  }

  return [
    annotation.medicationData?.atc3rdLevelCode,
    medicationName,
    annotation.medicationData?.dosage,
    routeOfAdministration,
    usageType,
  ].map((v) => v || undefined);
}
export function getFormattedAnnotationString(annotation: MedicationAnnotation, formatters: Formatters) {
  const values = getFormattedAnnotationParts(annotation, formatters);
  return values.filter((v) => v).join(' - ');
}

export const isInRange = (values: Record<string, any>[], min: number, max: number) => {
  for (let i = 0; i < values.length; i++) {
    if (values[i].value <= min || values[i].value >= max) {
      return false;
    }
  }
  return true;
};

export function getRiskFactorMedicationLabel(riskFactor: any, tEnum?: (enumName: string, key: string) => string): string | null {
  const medicationLabel =
    (tEnum
      ? tEnum('NarrowTherapeuticIndexMedicationEnum', riskFactor?.narrowTherapeuticIndexMedication)
      : riskFactor?.narrowTherapeuticIndexMedication) ||
    riskFactor?.drugInteractionDetail ||
    riskFactor.highInteractionPotentialMedicationDetail;
  return medicationLabel || null;
}

export type RiskFactorDto = MerieSchema.components['schemas']['RiskFactorDto'];

export type RiskFactorGroup = {
  isGroup: true;
  groupType: string;
  label: RiskFactorLabel;
  children: RiskFactorDto[];
};

export type RiskFactorRow = (RiskFactorDto & {isGroup?: false}) | RiskFactorGroup;

const ORGAN_DYSFUNCTION_ORDER: Record<string, number> = {
  HEPATIC_INSUFFICIENCY: 1,
  RENAL_INSUFFICIENCY: 2,
};

const GROUPED_MEDICATION_ORDER: Record<string, number> = {
  CORTICOSTEROIDS: 1,
  NARROW_THERAPEUTIC_INDEX: 2,
  DRUG_WITH_HIGH_INTERACTION_POTENTIAL: 3,
  DRUG_INTERACTION: 4,
};

export function groupAndSortRiskFactorsData(
  riskFactors: (RiskFactorDto & {translatedTypeOrUserComment?: string})[],
  oldRiskFactorIds: Set<number>
): RiskFactorRow[] {
  const organDysfunctions = riskFactors.filter((rf) => rf.labels[0] === 'ORGAN_DYSFUNCTION');
  const diagnoses = riskFactors.filter((rf) => rf.labels[0] === 'DIAGNOSIS');
  const medications = riskFactors.filter((rf) => rf.labels[0] === 'MEDICATION');
  const others = riskFactors.filter((rf) => rf.labels[0] === 'OTHER');

  const sortedOrgan = [...organDysfunctions].sort((a, b) => {
    const orderA = ORGAN_DYSFUNCTION_ORDER[a.type] ?? 99;
    const orderB = ORGAN_DYSFUNCTION_ORDER[b.type] ?? 99;
    return orderA - orderB;
  });

  const sortedDiagnoses = [...diagnoses].sort((a, b) => {
    const nameA = (a as any).translatedTypeOrUserComment ?? a.type;
    const nameB = (b as any).translatedTypeOrUserComment ?? b.type;
    return nameA.localeCompare(nameB);
  });

  const corticosteroids = medications.filter((rf) => rf.type === 'CORTICOSTEROIDS');
  const grouped: RiskFactorRow[] = [];

  Object.keys(GROUPED_MEDICATION_ORDER).forEach((groupType) => {
    if (groupType === 'CORTICOSTEROIDS') {
      corticosteroids.forEach((rf) => grouped.push(rf as RiskFactorDto & {isGroup?: false}));
      return;
    }
    const children = medications.filter((rf) => rf.type === groupType);
    if (children.length > 0) {
      grouped.push({
        isGroup: true,
        groupType,
        label: 'MEDICATION',
        children: [...children].sort((a, b) => {
          const labelA = getRiskFactorMedicationLabel(a) ?? '';
          const labelB = getRiskFactorMedicationLabel(b) ?? '';
          return labelA.localeCompare(labelB);
        }),
      });
    }
  });

  const rows: RiskFactorRow[] = [
    ...sortedOrgan.map((rf) => rf as RiskFactorDto & {isGroup?: false}),
    ...sortedDiagnoses.map((rf) => rf as RiskFactorDto & {isGroup?: false}),
    ...grouped,
    ...others.map((rf) => rf as RiskFactorDto & {isGroup?: false}),
  ];

  return rows;
}
